import { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueries } from '@tanstack/react-query';

import WizardStateContext from 'components/Wizards/shared/context/wizardstate';
import { REPORT_TYPES } from 'components/Wizards/Dashlet/types';
import {
  StyledStepHeader,
  StyledWizardField,
} from 'components/Wizards/shared/styles';
import MultiSelect from 'components/Inputs/MultiSelect';
import { KizenTypography } from '__app/typography';
import Loader from 'components/Kizen/Loader';

import { CUSTOM_OBJECTS, PAGE_CONFIGS } from 'queries/query-keys';
import CustomObjectsService from 'services/CustomObjectsService';
import TeamMemberService from 'services/TeamMemberService';
import {
  useActivitiesQuery,
  useDefaultActivityRelationshipsQuery,
} from 'queries/models/activites';

import { mapRelatedColumns } from 'components/Charts/ScheduledActivities/helpers';
import { defaultColumnSettings } from 'components/Charts/ScheduledActivities/constants';
import { ScheduledActivitiesColumnBuilder } from './ScheduledActivitiesColumnBuilder';
import { mapColumnSettings } from 'components/Charts/ScheduledActivities/helpers.js';
import useMetadataKey from 'components/Wizards/shared/hooks/useMetadataKey';
import { IconWithTooltip } from 'pages/Common/components/LinkWithIconTooltip/IconWithTooltip';
import styled from '@emotion/styled';

const IconWithTooltipStyled = styled(IconWithTooltip)`
  padding: 0 0 10px 0;
  align: base-line;
  margin-left: 5px;
  color: var(--color-font-placeholder);
  :hover {
    color: var(--color-background-darker);
  }
`;
export const ScheduledActivitiesReportType = ({
  scrollActvitiyBuilderIntoView,
}) => {
  const { t } = useTranslation();
  const { state, setState } = useContext(WizardStateContext.Context);
  const {
    dashletId,
    reportType,
    activities,
    hasDeletedActivities,
    scheduledActivitiesConfig,
  } = state;
  const selectRef = useRef(null);

  const { data, isLoading } = useActivitiesQuery({
    select: (results) => {
      return results.map((activity) => {
        return {
          label: activity.name,
          value: activity.id,
        };
      });
    },
  });

  const activitiesValue = useMemo(() => {
    return (
      activities?.map(({ label, value }) => {
        if (!isLoading && !data?.find((item) => item.value === value)) {
          return {
            label: t('[Deleted] - {{label}}', { label }),
            value,
            deleted: true,
          };
        }
        return { label, value };
      }) ?? []
    );
  }, [activities, t, data, isLoading]);

  useMetadataKey(
    'hasDeletedActivities',
    activitiesValue.some((item) => item.deleted)
  );

  const activitiesOnChange = useCallback(
    (value) => {
      setState('activities', value);
    },
    [setState]
  );

  const activityPageListQuery = useQuery({
    queryKey: PAGE_CONFIGS.SCHEDULED_ACTIVITIES,
    queryFn: () => TeamMemberService.getActivitiesListPageConfig(),
    enabled: !scheduledActivitiesConfig,
  });

  const savedColumnSettings = useMemo(() => {
    if (scheduledActivitiesConfig && !activityPageListQuery.isLoading) {
      return scheduledActivitiesConfig.columns;
    }
    return activityPageListQuery?.data?.columns;
  }, [
    activityPageListQuery.data,
    activityPageListQuery.isLoading,
    scheduledActivitiesConfig,
  ]);

  const defaultActivityRelationshipsQuery =
    useDefaultActivityRelationshipsQuery();
  const { relatedObjects = null } = useMemo(() => {
    if (defaultActivityRelationshipsQuery.isLoading) {
      return {};
    }
    const doa = defaultActivityRelationshipsQuery.data;
    return {
      relatedObjects: mapRelatedColumns(doa),
    };
  }, [
    defaultActivityRelationshipsQuery.data,
    defaultActivityRelationshipsQuery.isLoading,
  ]);

  const columnSettings = useMemo(() => {
    return mapColumnSettings(savedColumnSettings, relatedObjects, t);
  }, [savedColumnSettings, relatedObjects, t]);

  const columnAssociatedObjectFieldIds = useMemo(() => {
    return columnSettings.columns
      .filter(({ fieldId }) => fieldId)
      .map(({ fieldId, objectId }) => ({
        fieldId,
        objectId,
      }));
  }, [columnSettings]);

  const objectQueries = useQueries({
    queries: columnAssociatedObjectFieldIds.map(({ objectId }) => ({
      queryKey: CUSTOM_OBJECTS.DETAILS(objectId),
      queryFn: () =>
        CustomObjectsService.getCustomObjectDetails(objectId).catch(() => ({
          id: 'not_found',
          fields: [],
          name: '',
          description: '',
        })),
      enabled: Boolean(objectId),
    })),
  });

  const objectsById = useMemo(() => {
    const result = {};
    for (const query of objectQueries) {
      if (query.data && query.data.id !== 'not_found') {
        const data = query.data;
        result[data.id] = {
          ...data,
          fieldsById: data.fields.reduce((acc, field) => {
            acc[field.id] = field;
            return acc;
          }, {}),
        };
      }
    }
    return result;
  }, [objectQueries]);

  const isObjectsByIdLoading = useMemo(
    () => objectQueries.some((q) => q.isLoading),
    [objectQueries]
  );

  const filteredColumnSettings = useMemo(() => {
    const columnFilter = ({ fieldId, objectId, visible }) =>
      !fieldId || objectsById?.[objectId]?.fieldsById?.[fieldId] || !visible;
    const activeColumns = columnSettings.columns.filter(columnFilter);
    return {
      ...columnSettings,
      columns: activeColumns,
    };
  }, [columnSettings, objectsById]);

  if (reportType?.value !== REPORT_TYPES.SCHEDULED_ACTIVITIES) {
    return null;
  }

  return (
    <>
      <StyledWizardField top={12}>
        <StyledStepHeader>
          <KizenTypography type="subheader">
            {t('Choose Activities to Display (Leave Blank for All)')}
          </KizenTypography>
          <IconWithTooltipStyled
            label={t(
              'Only selected activities will be available for scheduling from this dashlet'
            )}
            icon="info-circle"
            color="var(--color-button-tertiary-hover)"
          />
        </StyledStepHeader>
        <MultiSelect
          ref={selectRef}
          isLoading={isLoading}
          onChange={activitiesOnChange}
          placeholder={t('Find Activities')}
          className="modal-multiselect activity-types"
          options={data ?? []}
          value={activitiesValue}
          error={hasDeletedActivities}
        />
      </StyledWizardField>
      <StyledWizardField flex>
        {!activityPageListQuery?.isLoading &&
          !defaultActivityRelationshipsQuery.isLoading &&
          !isObjectsByIdLoading && (
            <ScheduledActivitiesColumnBuilder
              displayHeader
              scrollActvitiyBuilderIntoView={scrollActvitiyBuilderIntoView}
              columns={filteredColumnSettings.columns}
              defaultColumns={defaultColumnSettings}
              scheduledActivitiesConfig={scheduledActivitiesConfig}
              displayDefaultColumns={!dashletId}
            />
          )}
        <Loader
          loading={
            activityPageListQuery?.isLoading ||
            defaultActivityRelationshipsQuery.isLoading ||
            isObjectsByIdLoading
          }
        />
      </StyledWizardField>
    </>
  );
};
